<template>
  <div class="offers-form" v-if="offer && offer.id">
    <div class="row">
      <div class="col-md-6">
        <OfferFormTextTabs
          :domains="domains"
          :fields="translatableFields"
          :form="form"
          :active-name.sync="activeName"
          :tab-label="tabLabel"
          :field-label="fieldLabel"
        />

        <OfferFormLinkedCatalog
          :form="form"
          :operations="operations"
          :illnesses="illnesses"
          :catalog-entity-label="catalogEntityLabel"
        />

        <div class="offers-form__actions mt-4">
          <button class="btn btn-primary" type="button" @click="save">Save Changes</button>
          <router-link :to="{ name: 'offers-list' }" class="btn btn-default">
            Back to list
          </router-link>
        </div>
      </div>

      <div class="col-md-6">
        <div class="mb-4">
          <OfferFormSettingsCard
            :form="form"
            :tags-input.sync="tagsInput"
            :currencies="currencies"
            :level-options="levelOptions"
          />
        </div>

        <div class="mb-4">
          <OfferFormMedicalServicesCard :form="form" :grouped-services="groupedServices">
            Select all service types here (medical, accommodation, transfer, common, etc.). In the text
            tabs you can override Accommodation, Transport, Other Services, and Implant per language; if
            a field is left empty, names from this list are used. Catalog:
            <router-link :to="{ name: 'offer-services-list' }">Offer services</router-link>.
          </OfferFormMedicalServicesCard>
        </div>

        <div v-if="offer.clinic" class="mb-4">
          <card title="Info">
            <p class="text-muted small mb-1">
              <b>Clinic:</b>
              {{ offer.clinic.name && offer.clinic.name.en ? offer.clinic.name.en : offer.clinic.id }}
            </p>
            <p class="text-muted small mb-1" v-if="offer.created_at">
              <b>Created:</b> {{ formatDate(offer.created_at) }}
            </p>
            <p class="text-muted small mb-0">
              <b>ID:</b> {{ offer.id }}
            </p>
          </card>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="text-center py-5">
    <span>Loading...</span>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import OfferFormTextTabs from "@/components/Offers/OfferFormTextTabs.vue";
import OfferFormSettingsCard from "@/components/Offers/OfferFormSettingsCard.vue";
import OfferFormMedicalServicesCard from "@/components/Offers/OfferFormMedicalServicesCard.vue";
import OfferFormLinkedCatalog from "@/components/Offers/OfferFormLinkedCatalog.vue";
import { API_FIELD_MAP, LEVEL_OPTIONS, SERVICE_GROUP_LABELS, TRANSLATABLE_FIELDS } from "./offerFormConstants";
import { buildOfferPayload, emptyLangs, readApiField } from "./offerPayload";
import {
  groupOfferServices,
  offerCatalogEntityLabel,
  offerDomainLanguages,
  offerFieldLabel,
  offerTabLabel
} from "./offerFormUi";

export default {
  components: {
    OfferFormTextTabs,
    OfferFormSettingsCard,
    OfferFormMedicalServicesCard,
    OfferFormLinkedCatalog
  },
  props: {
    id: { type: String, required: true }
  },
  data: () => ({
    activeName: null,
    tagsInput: "",
    levelOptions: LEVEL_OPTIONS,
    translatableFields: TRANSLATABLE_FIELDS,
    form: {
      type: "procedure",
      level: "1",
      is_hide: false,
      template_id: "",
      score: null,
      name: {},
      for_who: {},
      duration: {},
      procedure_description: {},
      accommodation: {},
      transport: {},
      other_services: {},
      post_operation_care: {},
      implant: {},
      promo_description: {},
      services: [],
      packagePrice: null,
      currency_code: "",
      operations: [],
      illnesses: []
    }
  }),
  computed: {
    ...mapState("offers", ["offer"]),
    ...mapState("offerServices", ["servicesList"]),
    ...mapState("cmsApiCatalogs", ["domains", "operations", "illnesses"]),
    ...mapState("data", ["currencies"]),
    domainLanguages() {
      return offerDomainLanguages(this.domains);
    },
    groupedServices() {
      return groupOfferServices(this.servicesList, SERVICE_GROUP_LABELS);
    }
  },
  watch: {
    "form.type"(next, prev) {
      if (next === prev || prev === undefined) return;
      if (next === "procedure") {
        this.form.illnesses = [];
      } else if (next === "illness") {
        this.form.operations = [];
      }
    }
  },
  async created() {
    this.$store.dispatch("setLoader", true);
    try {
      await this.getDomains();
      if (this.domains.length) {
        this.activeName = offerTabLabel(this.domains[0].language);
      }
      this.resetLangFields();
      await Promise.all([
        this.getOffer(this.id).then(() => this.populateForm()),
        this.getServices({}),
        this.getCurrencies(),
        this.getOperations(),
        this.getIllnesses({ perPage: 1500 })
      ]);
    } finally {
      this.$store.dispatch("setLoader", false);
    }
  },
  beforeDestroy() {
    this.SET_DATA({ type: "offer", data: {} });
    this.SET_DATA({ type: "offerRaw", data: {} });
  },
  methods: {
    ...mapActions("offers", ["getOffer", "updateOffer"]),
    ...mapActions("offerServices", ["getServices"]),
    ...mapActions("cmsApiCatalogs", ["getDomains", "getOperations", "getIllnesses"]),
    ...mapActions("data", ["getCurrencies"]),
    ...mapMutations("offers", ["SET_DATA"]),
    tabLabel: offerTabLabel,
    fieldLabel: offerFieldLabel,
    catalogEntityLabel: offerCatalogEntityLabel,
    resetLangFields() {
      const defaults = emptyLangs(this.domainLanguages);
      Object.keys(API_FIELD_MAP).forEach((field) => {
        this.$set(this.form, field, { ...defaults, ...(this.form[field] || {}) });
      });
      if (!this.domains || !this.domains.length) return;
      const names = this.domains.map((d) => offerTabLabel(d.language));
      if (!names.includes(this.activeName)) {
        this.activeName = names[0];
      }
    },
    populateForm() {
      const o = this.offer;
      if (!o) return;

      this.form.type = o.type || "procedure";
      this.form.level = String(o.level || "1");
      this.form.is_hide = !!o.is_hide;
      this.form.template_id = o.template_id || "";
      this.form.score = o.score || null;
      this.tagsInput = Array.isArray(o.tags) ? o.tags.join(", ") : "";

      const langDefaults = emptyLangs(this.domainLanguages);
      Object.keys(API_FIELD_MAP).forEach((field) => {
        const source = readApiField(o, field);
        this.$set(this.form, field, { ...langDefaults, ...source });
      });

      this.form.services = Array.isArray(o.services) ? [...o.services] : [];
      this.form.operations = Array.isArray(o.operations) ? [...o.operations] : [];
      this.form.illnesses = Array.isArray(o.illnesses) ? [...o.illnesses] : [];

      const rawPrice = o.packagePrice != null ? o.packagePrice : o.package_price;
      this.form.packagePrice =
        rawPrice != null && rawPrice !== "" ? Number(rawPrice) : null;
      const cur = o.currency || {};
      this.form.currency_code = cur.code ? String(cur.code) : "";
    },
    save() {
      if (!this.form.name.en) {
        return this.$notify({ type: "warning", title: "Name EN is required" });
      }

      const patch = buildOfferPayload(
        this.form,
        this.tagsInput,
        this.domainLanguages,
        this.currencies
      );

      this.$store.dispatch("setLoader", true);
      return this.updateOffer({ id: this.id, patch })
        .then(() => this.$notify({ type: "success", title: "Offer saved" }))
        .catch(() => this.$notify({ type: "warning", title: "Error saving offer" }))
        .finally(() => this.$store.dispatch("setLoader", false));
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-GB");
    }
  }
};
</script>

<style scoped>
.offers-form__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
</style>
